import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import type { CountryCode } from 'libphonenumber-js';
import parsePhoneNumberFromString, {
  ParseError,
  parsePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import type { Dispatch, SetStateAction } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { CountryCodeEnum, LanguageSlugEnum } from '@/types/regionTypes';
import useMemoizedFn from '@/utils/useMemoizedFn';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import { ConditionalWrapper } from '@/components/ConditionalWrapper';
import { Flex } from '@/components/Flex';
import {
  getTitleOptionByValue,
  getUserTitleLabel,
  TitleField,
} from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { Icon } from '@/components/Icon';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { Loader } from '@/components/Loader';
import { navToFindStores } from '@/components/Megamenu/helpers';
import { Modal } from '@/components/Modal';
import { ReCaptcha } from '@/components/ReCaptcha';
import { Select } from '@/components/Select';
import { SelectField } from '@/components/SelectField';
import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { TextAreaField } from '@/components/TextAreaField';
import { Typography } from '@/components/Typography';
import type { CountryCode as SaleorCountryCode } from '@/graphql/saleor/api';
import { useBoutiqueStock } from '@/hooks/useBoutiqueStock';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdDown, useIsSmUp } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import type { StoreHit } from '@/integrations/algolia/types';
import { getMetaValue } from '@/lib/address';
import type { DateInfo } from '@/lib/boutique/helpers';
import { formatTimesOption, reorderStores } from '@/lib/boutique/helpers';
import {
  CONTENTSQUARE_MASKED,
  EMAIL_REGX,
  MAX_CHAR_PHONE,
} from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { formatNationalByPhonePrefix, getExamplePhone } from '@/lib/phone';
import {
  getPhonePrefixSelectOptByCode,
  getPhonePrefixSelectOpts,
} from '@/lib/phonePrefixes/helpers';
import { getRegionFromLocale } from '@/lib/regions';
import {
  commonMessages as appointmentMessages,
  ConfirmLegal,
  KeepInformed,
} from '@/messages/appointment';
import { commonMessages } from '@/messages/common';
import { formMessages, PersonalDataPolicy } from '@/messages/forms';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { RetailerOption } from '@/views/AppointmentView/helpers';
import {
  createSearchOptions,
  fetchAppointment,
  getRetailerOptsFromSearchResults,
  handleCalendarMonthChangeUtil,
  makeCityOptions,
  makeRegionOptions,
  useBoutiqueSelect,
  useCalendarMonthTranslation,
} from '@/views/AppointmentView/helpers';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';
import { getNameFieldOrder, SortedNameFields } from '@/views/SignUpView/helper';
import { getStockDisplay } from '@/views/WatchView/components/BookAppointmentModal/utils';

import * as S from './BookAppointmentModal.styles';

const today = new Date();

const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);
export interface BookAppointmentProps {
  isOpen: boolean;
  name?: string;
  onClose: () => void;
  product?: any;
  sku?: string | null;
  variantDetails?: any;
}
type Schedule = {
  [day: string]: {
    endTime: string;
    startTime: string;
  }[];
} | null;
export type BookAppointmentModalState =
  | 'boutique'
  | 'appointment'
  | 'continueBrowsing';

const ImageCoverWrapper = (children: React.ReactNode) => (
  <S.ImageCover>{children}</S.ImageCover>
);
export const BookAppointmentModal = ({
  isOpen,
  name,
  onClose,
  product,
  sku,
  variantDetails,
}: BookAppointmentProps) => {
  const { formatMessage } = useIntl();
  const { locale, push } = useRouter();
  const { sendEvent, sendGenerateLeadEvent } = useGoogleAnalytics();
  const {
    locale: { sfCode },
  } = getRegionFromLocale(locale);
  const {
    country,
    locale: regionProviderLocale,
    query: { languageCode },
  } = useRegionProvider();
  const schema = yup.object().shape({
    firstName: getNameSchema(formatMessage, 'first'),
    lastName: getNameSchema(formatMessage, 'last'),
    ...(country.code === 'JP' && {
      firstNameKanji: getNameSchema(formatMessage, 'firstKanji'),
      lastNameKanji: getNameSchema(formatMessage, 'lastKanji'),
    }),
    email: yup
      .string()
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .required(formatMessage(appointmentMessages.requireEmail))
      .default(''),
    phonePrefix: yup
      .object({
        value: yup
          .string()
          .required(formatMessage(appointmentMessages.requirePhonePrefix)),
        code: yup
          .string()
          .required(formatMessage(appointmentMessages.requirePhonePrefix)),
        label: yup
          .string()
          .required(formatMessage(appointmentMessages.requirePhonePrefix)),
      })
      .required(formatMessage(appointmentMessages.requirePhonePrefix)),
    phone: yup
      .string()
      .required(formatMessage(appointmentMessages.requirePhone))
      .max(MAX_CHAR_PHONE, formatMessage(appointmentMessages.maxPhoneCharMsg))
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix?.value,
        then: (schema) =>
          schema.test(
            'is-valid-phone',
            formatMessage(formMessages.invalidPhoneNumber),
            function (value) {
              const { phonePrefix } = this.parent;

              if (!phonePrefix.value) {
                return false;
              }

              const fullPhoneNumber = `${phonePrefix.value}${value}`;

              const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
              if (!phoneNumber) {
                return false;
              }

              const isValid = phoneNumber.isValid();
              return isValid;
            },
          ),
      }),
    reason: yup.object().default({
      label: formatMessage(appointmentMessages.discover_and_try),
      value: 'discover_and_try',
    }),
    title: getTitleSchema(formatMessage, country.code),
    timeSlot: yup.object(),
    comments: yup
      .string()
      .max(3000, formatMessage(appointmentMessages.maxCommentsMsg))
      .default(''),
    newsletterOptIn: yup.bool().default(false),
    consent: yup
      .bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
    captcha: yup
      .bool()
      .default(false)
      .oneOf([true], formatMessage(appointmentMessages.requireField)),
  });
  const [utc_offset, setUtc_offset] = useState('');
  const [holidayHours, setHolidayHours] = useState<DateInfo[] | undefined>();
  const [reopenDate, setReopenDate] = useState<string | undefined>();
  const [nonWorkingDays, setNonWorkingDays] = useState<string[] | undefined>();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [locationId, setLocationId] = useState<string>();
  const [ImageUrl, setImageUrl] = useState<string>('');
  const [boutiqueName, setBoutiqueName] = useState<string>('');
  const [modalState, setModalState] =
    useState<BookAppointmentModalState>('boutique');

  const [date, setDate] = useState(dayjs(tomorrow).format('YYYY-MM-DD'));
  const [appointments, setAppointments] = useState<Schedule>();
  const [availableTimes, setAvailableTimes] = useState([
    { label: '11:00 - 12:00', value: '11:00 - 12:00' },
    { label: '12:00 - 13:00', value: '12:00 - 13:00' },
    { label: '13:00 - 14:00', value: '13:00 - 14:00' },
    { label: '14:00 - 15:00', value: '14:00 - 15:00' },
    { label: '15:00 - 16:00', value: '15:00 - 16:00' },
  ]);
  const [fullName, setFullName] = useState('');
  const [activeStartMonth, setActiveStartMonth] = useState(
    tomorrow.getMonth() + 1,
  );
  useCalendarMonthTranslation(
    activeStartMonth,
    regionProviderLocale.sfCode,
    modalState,
  );
  const isMdDown = useIsMdDown();
  const isSmUp = useIsSmUp();

  const [isVariantDetails, setIsVariantDetails] = useState<
    | {
        name: string;
        pricing: {
          price: {
            currency: string;
            gross: { amount: number };
            net: { amount: number };
          };
        };
        product: {
          caseMaterial: { values: [{ name: string }] };
          dialColor: { values: [{ name: string }] };
        };
      }
    | undefined
  >(undefined);
  const {
    snapshot: { user },
  } = useUser();

  const formCtx = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });
  const captcha = formCtx.watch('captcha');
  const phonePrefix = formCtx.watch('phonePrefix');
  const phone = formCtx.watch('phone');
  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);
  useEffect(() => {
    if (modalState === 'appointment') {
      formCtx.setValue('title', null);
      if (user) {
        formCtx.reset({
          ...user,
          firstNameKanji: getMetaValue(
            ACCOUNT_METADATA.first_name_kanji,
            user.metadata ?? [],
          ),
          lastNameKanji: getMetaValue(
            ACCOUNT_METADATA.last_name_kanji,
            user.metadata ?? [],
          ),
        });
        const personalTitle = user?.metadata?.find(
          (item) => item.key === 'user.personalTitle',
        )?.value;
        const titleOption = getTitleOptionByValue(
          formatMessage,
          country.code,
          personalTitle,
        );
        formCtx.setValue('title', titleOption);
      } else {
        formCtx.setValue('title', null);
      }
      if (!phonePrefix)
        formCtx.setValue(
          'phonePrefix',
          getPhonePrefixSelectOptByCode(country.code),
        );
    }
  }, [user, modalState]);
  useEffect(() => {
    if (!user) return;

    const userPhone = user?.metadata?.find(
      (item) => item.key === 'user.phone',
    )?.value;
    userPhone && formCtx.setValue('phone', userPhone);

    const phonePrefix = user?.metadata?.find(
      (item) => item.key === 'user.phonePrefix',
    )?.value;

    const countryCode: CountryCode =
      (phonePrefix &&
        userPhone &&
        parsePhoneNumber(phonePrefix + userPhone)?.country) ||
      (country.code as CountryCode);

    countryCode &&
      formCtx.setValue(
        'phonePrefix',
        getPhonePrefixSelectOptByCode(
          (countryCode as SaleorCountryCode) || country.code,
        ),
      );
  }, [user, modalState]);
  useEffect(() => {
    if (appointments) {
      formatTimesOption(
        appointments,
        date,
        holidayHours,
        setAvailableTimes,
        setNonWorkingDays,
        reopenDate,
      );
    }
  }, [date, appointments]);
  useEffect(() => {
    if (isOpen && variantDetails) {
      setIsVariantDetails(variantDetails);
    }
  }, [isOpen, variantDetails]);
  useEffect(() => {
    if (availableTimes) formCtx.setValue('timeSlot', availableTimes[0]);
  }, [availableTimes]);

  useEffect(() => {
    if (modalState !== 'boutique') {
      setModalState('boutique');
    }
  }, [isOpen]);

  const futureDate = new Date(tomorrow);
  futureDate.setDate(tomorrow.getDate() + 29);
  const onChange = (newDate: any) => {
    setDate(dayjs(newDate).format('YYYY-MM-DD'));
  };
  const handleMonthChange = handleCalendarMonthChangeUtil(setActiveStartMonth);
  const submit = async (data: any) => {
    setProcessing(true);
    data = { ...data, date: date };
    const time = date + data.timeSlot.value;
    const isoTime = dayjs(time).format('YYYY-MM-DDTHH:mm:ss');
    const startDateTime = isoTime + utc_offset;

    const AppointmentData = {
      salutation: data.title?.value ?? '',
      phone: data.phonePrefix.value + data.phone,
      reason: data.reason.value,
      websiteCountryCode: country.code,
      languageCode: sfCode,
      locationId: locationId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      comments: data.comments,
      reserveProductSku: sku,
      startDateTime: startDateTime,
      newsletterOptIn: data.newsletterOptIn,
      ...(!!data.firstNameKanji && { firstNameKanji: data.firstNameKanji }),
      ...(!!data.lastNameKanji && { lastNameKanji: data.lastNameKanji }),
    };
    try {
      const appointmentRes = await fetchAppointment(AppointmentData);
      if (appointmentRes.success) {
        sendGenerateLeadEvent({
          appointmentId: appointmentRes.requestId,
          appointmentMotive: AppointmentData.reason,
          dateAppointment: AppointmentData.startDateTime,
          sku: sku as string,
          storeId: AppointmentData.locationId as string,
        });
        setProcessing(false);
        const titleLabel = getUserTitleLabel(country.code, data.title);
        setFullName(`${titleLabel}${data.firstName} ${data.lastName}`);
        setModalState('continueBrowsing');
      } else {
        setProcessing(false);
        setError(appointmentRes.type);
      }
    } catch (error: any) {
      setProcessing(false);
      setError(error.message);
    }
  };

  useEffect(() => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.bookAppointment,
      });
    }
  }, [isOpen]);

  const handleModalClose = () => {
    sendEvent({
      event: EVENT_NAME.POPIN_CLOSE,
      popin_title: popinTitle.bookAppointment,
    });

    onClose();
  };
  const [examplePhone, setExamplePhone] = useState<string>('');

  useEffect(() => {
    if (phonePrefix?.code) {
      const phoneNumberExample = getExamplePhone(
        phonePrefix.code as CountryCode,
      );
      if (phoneNumberExample) setExamplePhone(phoneNumberExample);
    }
  }, [phonePrefix]);

  useEffect(() => {
    if (phone && phonePrefix?.value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phone,
          phonePrefix.code as CountryCode,
        );
        formCtx.setValue(
          'phone',
          formatNationalByPhonePrefix(
            phonePrefix.value,
            phoneNumber.formatNational(),
          ),
        );

        formCtx.clearErrors('phone');
      } catch (error) {
        if (error instanceof ParseError) {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.invalidPhoneNumber),
          });
        } else {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.technicalError),
          });
        }
      }
    }
  }, [phone, phonePrefix, formCtx, formatMessage]);
  const aspectRatioX = isMdDown ? 375 : 640;
  const isJapan = country.code === CountryCodeEnum.JP;

  const productType = product?.productType?.slug;

  return (
    <S.Modal
      gutters={false}
      isOpen={isOpen}
      onClose={handleModalClose}
      overflowHidden={modalState === 'continueBrowsing'}
    >
      <S.ModalHeaderWrapper>
        <S.ModalHeader>
          <Modal.Header data-testid='isOpen' onClose={handleModalClose}>
            {modalState === 'boutique'
              ? formatMessage(appointmentMessages.findAStore)
              : formatMessage(commonMessages.bookAppointment)}
          </Modal.Header>
        </S.ModalHeader>
      </S.ModalHeaderWrapper>
      {modalState === 'boutique' && (
        <Box bg='lightGrey'>
          <Modal.Body>
            <BoutiqueState
              isOpen={isOpen}
              productSku={sku ?? ''}
              productType={productType}
              setAppointments={setAppointments}
              setBoutiqueName={setBoutiqueName}
              setHolidayHours={setHolidayHours}
              setImageUrl={setImageUrl}
              setLocationId={setLocationId}
              setModalState={setModalState}
              setReopenDate={setReopenDate}
              setUtc_offset={setUtc_offset}
            />
          </Modal.Body>
        </Box>
      )}

      {modalState === 'appointment' && (
        <Box bg='lightGrey'>
          <Modal.Body>
            <S.ProductBannerWrapper>
              <S.BannerImageBox>
                <S.ProductImage
                  alt='product'
                  height={0}
                  sizes='100vw'
                  src={product?.media[0]?.url ?? ''}
                  width={0}
                />
              </S.BannerImageBox>
              <S.ProductHeaderDesc>
                <Typography color='secondaryGrey'>{sku ? sku : ''}</Typography>
                <Typography color='black' fontWeight='bold' scale='p1'>
                  {name?.toUpperCase() ? name?.toUpperCase() : ''}
                </Typography>
                <Typography color='primaryBlue'>
                  {[
                    isVariantDetails?.product?.caseMaterial?.values[0]?.name,
                    isVariantDetails?.product?.dialColor?.values[0]?.name,
                  ]
                    .filter(Boolean)
                    .join('-')}
                </Typography>
                <Typography color='primaryBlue'>
                  {`${isVariantDetails?.pricing?.price?.currency ?? ''} ${
                    isVariantDetails?.pricing?.price?.gross?.amount ??
                    isVariantDetails?.pricing?.price?.net?.amount ??
                    ''
                  }`}
                </Typography>
              </S.ProductHeaderDesc>
            </S.ProductBannerWrapper>
            <S.HeroWrapper>
              <S.HeroText>
                <S.HeroHeading
                  color='white'
                  fontWeight='bold'
                  scale='h2'
                  textTransform='uppercase'
                >
                  {boutiqueName}
                </S.HeroHeading>
                <S.ButtonWrapper>
                  <Button
                    data-testid='toBoutiqueButton'
                    isFullWidthMobile={false}
                    onClick={() => {
                      setModalState('boutique');
                    }}
                  >
                    {formatMessage(appointmentMessages.changeStoreButtonText)}
                  </Button>
                </S.ButtonWrapper>
              </S.HeroText>
              <ConditionalWrapper
                condition={!ImageUrl}
                wrapper={ImageCoverWrapper}
              >
                <AspectRatioImage
                  alt='boutique'
                  aspectRatio={{ x: aspectRatioX, y: isSmUp ? 192 : 250 }}
                  className='boutique-image'
                  data-testid='aspectRatioImage'
                  focalPoint={{ x: 1, y: 1 }}
                  loaderProps={{ height: 245 }}
                  loading='lazy'
                  originalImageDimensions={undefined}
                  showFallbackPlaceholder={false}
                  showPlaceholderBackground={true}
                  sizes='(max-width: 600px) 50vw, 20vw'
                  src={ImageUrl}
                  useLoader
                  width='100%'
                />
              </ConditionalWrapper>
            </S.HeroWrapper>
            <Box>
              <FormProvider name='appointment' {...formCtx} onSubmit={submit}>
                <Box bg='white' mt='s125'>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    pt={4}
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.appointmentDate)}
                  </Typography>
                </Box>
                <S.CalendarWrapper bg='white'>
                  <S.CalendarStyled
                    formatMonthYear={(_, date) => dayjs(date).format('MMMM')}
                    formatShortWeekday={(_locale, value) =>
                      ['S', 'M', 'T', 'W', 'T', 'F', 'S'][value.getDay()]
                    }
                    locale='EN'
                    maxDate={futureDate}
                    minDate={tomorrow}
                    nextLabel={
                      <Icon
                        color='primary'
                        name='chevronRight'
                        size='l'
                        variant='regular'
                      />
                    }
                    onActiveStartDateChange={handleMonthChange}
                    onChange={onChange}
                    prevLabel={
                      <Icon
                        color='primary'
                        name='chevronLeft'
                        size='l'
                        variant='regular'
                      />
                    }
                    showNeighboringMonth={false}
                    tileDisabled={({ date }) => {
                      const formattedDate = dayjs(date).format('YYYY-MM-DD');
                      return nonWorkingDays?.includes(formattedDate) ?? false;
                    }}
                    value={date}
                  />
                </S.CalendarWrapper>
                <S.SameDayAppointmentWrapper bg='white'>
                  <S.SameDayAppointmentBox>
                    <Typography color='default' mb={4} mt={2} textAlign='left'>
                      {formatMessage(appointmentMessages.contactBoutiqueText)}
                    </Typography>
                  </S.SameDayAppointmentBox>
                </S.SameDayAppointmentWrapper>
                <S.TimeSlotWrapper pl={2} pr={2}>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mb={2}
                    mt={4}
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.timeSlotText)}
                  </Typography>
                  <S.TimeSlotCol>
                    <SelectField
                      name='timeSlot'
                      options={availableTimes}
                      required
                    />
                  </S.TimeSlotCol>
                </S.TimeSlotWrapper>
                <S.SameDayAppointmentWrapper>
                  <S.SameDayAppointmentBox>
                    <Typography color='default' mb={2} mt={2} textAlign='left'>
                      {formatMessage(appointmentMessages.validAppointmentText)}
                    </Typography>
                  </S.SameDayAppointmentBox>
                </S.SameDayAppointmentWrapper>
                <S.PersonalInfoBox pl={2} pr={2}>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    mb={2}
                    mt={4}
                    scale='h5'
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.personalInfo)}
                  </Typography>
                  <S.TitleFlex>
                    <Col>
                      <TitleField countryCode={country.code} />
                    </Col>
                  </S.TitleFlex>
                </S.PersonalInfoBox>
                <S.PersonalInfoBox pl={2} pr={2}>
                  <S.NamesBox>
                    <SortedNameFields
                      arr={[
                        {
                          component: (
                            <Col key='firstName'>
                              <InputField
                                className={CONTENTSQUARE_MASKED}
                                data-testid='firstname'
                                name='firstName'
                                placeholder={formatMessage(
                                  formMessages.firstName,
                                )}
                                required
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            regionProviderLocale,
                            'first',
                          ),
                        },
                        {
                          component: (
                            <Col key='lastName'>
                              <InputField
                                className={CONTENTSQUARE_MASKED}
                                data-testid='lastname'
                                name='lastName'
                                placeholder={formatMessage(
                                  formMessages.lastName,
                                )}
                                required
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            regionProviderLocale,
                            'last',
                          ),
                        },
                        ...(isJapan
                          ? [
                              {
                                component: (
                                  <Col key='firstNameKanji'>
                                    <InputField
                                      className={CONTENTSQUARE_MASKED}
                                      data-testid='firstNameKanji'
                                      name='firstNameKanji'
                                      placeholder={formatMessage(
                                        formMessages.firstNameKanji,
                                      )}
                                      required
                                    />
                                  </Col>
                                ),
                                order: getNameFieldOrder(
                                  country,
                                  regionProviderLocale,
                                  'firstKanji',
                                ),
                              },
                              {
                                component: (
                                  <Col key='lastNameKanji'>
                                    <InputField
                                      className={CONTENTSQUARE_MASKED}
                                      data-testid='lastNameKanji'
                                      name='lastNameKanji'
                                      placeholder={formatMessage(
                                        formMessages.lastNameKanji,
                                      )}
                                      required
                                    />
                                  </Col>
                                ),
                                order: getNameFieldOrder(
                                  country,
                                  regionProviderLocale,
                                  'lastKanji',
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </S.NamesBox>
                  <S.EmailFieldCol>
                    <InputField
                      className={CONTENTSQUARE_MASKED}
                      data-testid='email'
                      name='email'
                      placeholder={formatMessage(
                        appointmentMessages.emailPlaceHolder,
                      )}
                      required
                    />
                  </S.EmailFieldCol>
                  <S.PhoneFieldCol>
                    <SelectField
                      name='phonePrefix'
                      options={getPhonePrefixSelectOpts()}
                    />
                  </S.PhoneFieldCol>
                  <S.PhoneFieldCol>
                    <InputField
                      autoComplete='phone'
                      className={CONTENTSQUARE_MASKED}
                      data-testid='phone'
                      name='phone'
                      onChange={() => formCtx.trigger('phone')}
                      pattern='[0-9]+'
                      placeholder={formatMessage(
                        appointmentMessages.phonePlaceHolder,
                      )}
                      required
                    />
                    <Typography
                      color='secondaryGrey'
                      scale='p3'
                      textAlign='right'
                    >
                      {examplePhone}
                    </Typography>
                  </S.PhoneFieldCol>
                </S.PersonalInfoBox>
                <S.CommentsWrapper pl={2} pr={2}>
                  <Typography
                    color='secondary'
                    fontWeight='bold'
                    pt={4}
                    scale='h5'
                    textAlign='center'
                  >
                    {formatMessage(appointmentMessages.bookAppointmentTo)}
                  </Typography>
                  <S.ReasonCol>
                    <SelectField
                      defaultValue={{
                        label: formatMessage(
                          appointmentMessages.discover_and_try,
                        ),
                        value: 'discover_and_try',
                      }}
                      name='reason'
                      options={[
                        {
                          label: formatMessage(
                            appointmentMessages.discover_and_try,
                          ),
                          value: 'discover_and_try',
                        },
                        {
                          label: formatMessage(
                            appointmentMessages.find_a_new_strap,
                          ),
                          value: 'find_a_new_strap',
                        },
                        {
                          label: formatMessage(
                            appointmentMessages.take_care_of_my_breitling,
                          ),
                          value: 'take_care_of_my_breitling',
                        },
                        {
                          label: formatMessage(appointmentMessages.purchase),
                          value: 'purchase',
                        },
                        {
                          label: formatMessage(appointmentMessages.other),
                          value: 'other',
                        },
                      ]}
                      placeholder={formatMessage(
                        appointmentMessages.reasonPlaceHolder,
                      )}
                    />
                  </S.ReasonCol>
                  <S.TextAreaCol>
                    <TextAreaField
                      maxLength={1000}
                      name='comments'
                      placeholder={formatMessage(
                        appointmentMessages.tellUsSomething,
                      )}
                      rows={5}
                    />
                  </S.TextAreaCol>
                </S.CommentsWrapper>
                <S.LegalCheckBoxCol>
                  <CheckboxField name='newsletterOptIn'>
                    <S.LegalTextWrapper>
                      <S.OptinOptionsText>{KeepInformed}</S.OptinOptionsText>
                      <br />
                      <S.OptinOptionsText>
                        {PersonalDataPolicy}
                      </S.OptinOptionsText>
                    </S.LegalTextWrapper>
                  </CheckboxField>
                </S.LegalCheckBoxCol>
                <S.LegalCheckBoxCol>
                  <CheckboxField name='consent'>
                    <S.LegalTextWrapper>
                      <S.OptinOptionsText>{ConfirmLegal}</S.OptinOptionsText>
                    </S.LegalTextWrapper>
                  </CheckboxField>
                </S.LegalCheckBoxCol>
                <S.RecaptchaCol>
                  <ReCaptcha
                    languageCode={languageCode}
                    name='captcha'
                    onChange={setIsVerifyingRecaptchaStatus}
                  />
                </S.RecaptchaCol>
                <S.FlexButtonBox>
                  <S.AppointmentButton
                    data-testid='submitAppointment'
                    disabled={!captcha || isVerifyingRecaptcha || processing}
                    isProcessing={processing || isVerifyingRecaptcha}
                    type='submit'
                  >
                    {formatMessage(commonMessages.bookAppointment)}
                  </S.AppointmentButton>
                </S.FlexButtonBox>
                {error && (
                  <S.CommentsWrapper>
                    <Col>
                      <Typography color='error' textAlign='center'>
                        {error}
                      </Typography>
                    </Col>
                  </S.CommentsWrapper>
                )}
              </FormProvider>
            </Box>
          </Modal.Body>
        </Box>
      )}

      {modalState === 'continueBrowsing' && (
        <>
          <S.continueBrowsingDivider />
          <Box>
            <Modal.Body>
              <S.HeroWrapper>
                <S.HeroText>
                  <S.HeroHeading
                    color='white'
                    fontWeight='bold'
                    scale='h2'
                    textTransform='uppercase'
                  >
                    {boutiqueName}
                  </S.HeroHeading>
                </S.HeroText>
                <ConditionalWrapper
                  condition={!ImageUrl}
                  wrapper={ImageCoverWrapper}
                >
                  <AspectRatioImage
                    alt='boutique'
                    aspectRatio={{ x: aspectRatioX, y: 192 }}
                    className='boutique-image'
                    focalPoint={{ x: 1, y: 1 }}
                    loaderProps={{ height: 245 }}
                    loading='lazy'
                    originalImageDimensions={undefined}
                    showFallbackPlaceholder
                    showPlaceholderBackground={true}
                    sizes='(max-width: 600px) 50vw, 20vw'
                    src={ImageUrl}
                    useLoader
                    width='100%'
                  />
                </ConditionalWrapper>
              </S.HeroWrapper>
              <Box>
                <Typography
                  className={CONTENTSQUARE_MASKED}
                  color='secondary'
                  fontWeight='bold'
                  mb={2}
                  mt={4}
                  scale='h5'
                  textAlign='center'
                  textTransform='uppercase'
                >
                  {fullName}
                </Typography>
                <Typography color='default' textAlign='center'>
                  {formatMessage(appointmentMessages.gratitudeText)}
                </Typography>
                <Typography color='default' textAlign='center'>
                  {formatMessage(appointmentMessages.retailerInTouch)}
                </Typography>
              </Box>
              <S.FlexButtonBox>
                <S.DiscoverButton
                  data-testid='finalButton'
                  onClick={() => {
                    onClose();
                    push('/collections');
                  }}
                  size='l'
                >
                  {formatMessage(appointmentMessages.discoverCollections)}
                </S.DiscoverButton>
              </S.FlexButtonBox>
            </Modal.Body>
          </Box>
        </>
      )}
    </S.Modal>
  );
};
type ChildComponentProps = {
  isOpen: boolean;
  productSku: string;
  productType?: string;
  setAppointments: Dispatch<SetStateAction<Schedule | undefined>>;
  setBoutiqueName: Dispatch<SetStateAction<string>>;
  setHolidayHours: Dispatch<SetStateAction<DateInfo[] | undefined>>;
  setImageUrl: Dispatch<SetStateAction<string>>;
  setLocationId: Dispatch<SetStateAction<string | undefined>>;
  setModalState: Dispatch<
    SetStateAction<'boutique' | 'appointment' | 'continueBrowsing'>
  >;
  setReopenDate: Dispatch<SetStateAction<string | undefined>>;
  setUtc_offset: Dispatch<SetStateAction<string>>;
};
type optionType = {
  label: string;
  value: string;
};
export const BoutiqueState: React.FC<ChildComponentProps> = ({
  isOpen,
  productSku,
  productType,
  setAppointments,
  setBoutiqueName,
  setHolidayHours,
  setImageUrl,
  setLocationId,
  setModalState,
  setReopenDate,
  setUtc_offset,
}) => {
  const { sendEvent } = useGoogleAnalytics();
  const {
    country,
    locale: { slug: langSlug },
  } = useRegionProvider();
  const {
    boutiqueSelectState,
    handleChangeCity,
    handleChangeRegion,
    setBoutiqueSelectState,
  } = useBoutiqueSelect(langSlug);
  const [apiError, setApiError] = useState<string>('');
  const locale = useContext(LocaleContext);
  const {
    country: { code },
  } = useRegionProvider();
  const isSmUp = useIsSmUp();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const index = algoliaSearchClient.initIndex(getIndexName('STORE', locale));
  const { formatMessage } = useIntl();
  const { loading: loadingStock, stocks: boutiqueStocks } = useBoutiqueStock(
    country.code,
    productSku,
  );

  const initData = useMemoizedFn(async () => {
    setIsLoading(true);

    const searchOptions = createSearchOptions({
      countryCode: country.code,
      localeSlug: langSlug,
      offersAppointment: true,
      webVisible: true,
      productType,
    });

    try {
      const { hits } = await index.search('', searchOptions);
      const reorderedHits = reorderStores(hits as StoreHit[]);
      const sortedRegionOpts = makeRegionOptions(reorderedHits, formatMessage);
      const cityOptions = makeCityOptions(reorderedHits);
      const opts = getRetailerOptsFromSearchResults(reorderedHits, langSlug);
      setBoutiqueSelectState({
        boutiqueList: reorderedHits,
        regionOptions: sortedRegionOpts,
        boutiqueOptions: opts,
        cityOptions: cityOptions,
      });
    } catch (e) {
      if (e instanceof Error) setApiError(e.message);
      setBoutiqueSelectState({
        boutiqueList: [],
        regionOptions: [],
        cityOptions: [],
      });
    }
    setBoutiqueSelectState({
      city: null,
      boutique: null,
      region: null,
    });
    setIsLoading(false);
  });

  useEffect(() => {
    initData();
  }, [country.code, isOpen]);

  if (isLoading) return <Loader height={450} />;

  return (
    <>
      {boutiqueSelectState.boutiqueOptions.length === 0 ? (
        <>
          <Box padding={2}>
            <Typography color='default' pb={1} pt={1} textAlign='center'>
              {formatMessage(appointmentMessages.noAvailableBoutiques)}
            </Typography>
            <Typography color='default' textAlign='center'>
              {formatMessage(appointmentMessages.storeLocatorToBrowse)}
            </Typography>
          </Box>
          <S.StoreLocatorButton padding={2}>
            <LegacyButton
              aria-label='Store Locator'
              as='a'
              data-test-id='storeLocator'
              href={navToFindStores(locale, code)}
              isFluid
              target='_self'
            >
              {formatMessage(appointmentMessages.storeLocator)}
            </LegacyButton>
          </S.StoreLocatorButton>
        </>
      ) : (
        <>
          <Box mb='s125' ml='s200' mr='s200' mt='s125'>
            <Typography color='default' pt={3} textAlign='center'>
              {formatMessage(
                productType === 'eyewear'
                  ? appointmentMessages.boutiquesWithSunglasses
                  : appointmentMessages.boutiquesWithWatch,
              )}
            </Typography>
          </Box>
          <Box mb='s125' ml='s200' mr='s200' mt='s125'>
            <S.RegionCol>
              <Typography color='error' textAlign='center'>
                {apiError}
              </Typography>
            </S.RegionCol>
            {boutiqueSelectState.regionOptions.length > 0 && (
              <S.RegionCol>
                <Select
                  name='region'
                  onChange={(opt) => {
                    handleChangeRegion(opt as optionType);
                  }}
                  options={boutiqueSelectState.regionOptions}
                  placeholder={formatMessage(
                    appointmentMessages.selectRegionPlaceholder,
                  )}
                  value={boutiqueSelectState.region}
                />
              </S.RegionCol>
            )}
            <S.RegionCol>
              <Select
                name='city'
                onChange={(opt) => {
                  handleChangeCity(opt as optionType);
                }}
                options={boutiqueSelectState.cityOptions}
                placeholder={formatMessage(
                  appointmentMessages.selectCityPlaceholder,
                )}
                value={boutiqueSelectState.city}
              />
            </S.RegionCol>
          </Box>
          {boutiqueSelectState.boutiqueOptions?.map((opt: RetailerOption) => {
            const boutique = opt.data;
            const stock = boutiqueStocks?.find(
              (item) => item.location === opt.value,
            )?.stock_display;

            const { color: stockColor, message: stockMessage } =
              getStockDisplay(formatMessage, stock);

            const showJPAddress =
              country.code === CountryCodeEnum.JP &&
              langSlug === LanguageSlugEnum.ja;
            const street = getTranslatedStoreValue(
              boutique,
              'street',
              langSlug,
            );
            const city = getTranslatedStoreValue(boutique, 'city', langSlug);
            const region = getTranslatedStoreValue(
              boutique,
              'region',
              langSlug,
            );
            const postalCode = getTranslatedStoreValue(
              boutique,
              'postal_code',
              langSlug,
            );
            const addressLines = showJPAddress
              ? [`${postalCode} ${region} ${city},`, street]
              : [street, `${city}, ${region} ${postalCode}`];

            return (
              <Box key={opt.value}>
                <S.BoutiqueCardWrapper
                  bg='white'
                  mb={4}
                  ml='s200'
                  mr='s200'
                  mt={4}
                >
                  <Flex justifyContent='space-between'>
                    <Typography color='secondary' fontWeight='bold' scale='h5'>
                      {getTranslatedStoreValue(boutique, 'name', langSlug)}
                    </Typography>
                    {isSmUp && <S.breitlingIcon name='breitling' />}
                  </Flex>
                  <Flex flexDirection='column' mt={1}>
                    {addressLines.map((line, index) => (
                      <Typography key={index} color='secondary'>
                        {line}
                      </Typography>
                    ))}
                  </Flex>
                  <S.ContactFlex gridGap={1} justifyContent='space-between'>
                    <Flex alignItems='center' gridGap={2}>
                      <S.StyledIcon name='telephoneCall' size='l' />
                      <S.StyledLink href={`tel:${boutique?.phone}`}>
                        {boutique?.phone}
                      </S.StyledLink>
                    </Flex>
                    <Flex alignItems='center' gridGap={1}>
                      {loadingStock ? (
                        <Loader height={20} size='xs' />
                      ) : (
                        <>
                          <S.Dot color={stockColor} />
                          <S.CenteredSpan>{stockMessage}</S.CenteredSpan>
                        </>
                      )}
                    </Flex>
                  </S.ContactFlex>
                  <LegacyButton
                    data-testid='toAppointment'
                    isFluid
                    onClick={() => {
                      setAppointments(boutique?.appointments as Schedule);
                      setLocationId(boutique?.store_id);
                      setModalState('appointment');
                      setImageUrl(
                        boutique?.photo_gallery?.[1]?.image?.url ??
                          boutique?.photo_gallery?.[0]?.image?.url ??
                          '',
                      );
                      setHolidayHours(
                        boutique?.temporary_hours?.holiday_hours ?? undefined,
                      );
                      setReopenDate(
                        boutique?.temporary_hours?.reopen_date ?? undefined,
                      );
                      setUtc_offset(boutique?.utc_offset ?? '');
                      setBoutiqueName(
                        getTranslatedStoreValue(boutique, 'name', langSlug),
                      );
                      sendEvent({
                        event: EVENT_NAME.BOOK_AN_APPT,
                      });
                    }}
                    size='m'
                    style={{ wordWrap: 'break-word', maxHeight: 50 }}
                  >
                    <S.TextButton>
                      {formatMessage(commonMessages.bookAppointment)}
                    </S.TextButton>
                  </LegacyButton>
                </S.BoutiqueCardWrapper>
              </Box>
            );
          })}
        </>
      )}
    </>
  );
};
BookAppointmentModal.displayName = 'BookAppointmentModal';
